/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/react-ant
 * @description Renders the Ant Design-based React adapter configuration form.
 * This component is used inside adapter pages and dialogs to collect the
 * provider-specific credentials required for each infrastructure domain.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { FormEvent, JSX } from "react";
import { useState } from "react";

/**
 * Props accepted by the React adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property className Optional class name merged onto the form root
 * @property style Optional inline styles merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  className?: string;
  style?: React.CSSProperties;
  titleId?: string;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with the React variant
 */
export function AdapterConfigForm({
  domain,
  initialConfig,
  onSave,
  onCancel,
  className,
  style,
  titleId,
}: AdapterConfigFormProps): JSX.Element {
  const providers = getProvidersForDomain(domain);
  const [provider, setProvider] = useState(
    initialConfig?.provider ?? providers[0]?.id ?? "",
  );
  const [apiKey, setApiKey] = useState(initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = useState(initialConfig?.baseUrl ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { token } = theme.useToken();

  const selectedMeta = providers.find((entry) => entry.id === provider);
  const needsApiKey = selectedMeta?.requiresApiKey ?? false;
  const needsBaseUrl = selectedMeta?.requiresBaseUrl ?? false;

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey && !apiKey.trim()) {
      setError("API key is required");
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        provider,
        apiKey: apiKey || undefined,
        baseUrl: baseUrl || undefined,
      });
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getTierTokens = (tier: string) => {
    switch (tier) {
      case "pronto":
        return { bg: token.colorPrimaryBg, text: token.colorText };
      case "lancio":
        return { bg: token.colorWarningBg, text: token.colorText };
      default:
        return { bg: token.colorBgLayout, text: token.colorTextSecondary };
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: token.marginLG,
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorder}`,
        backgroundColor: token.colorBgContainer,
        padding: token.paddingLG,
        boxShadow: token.boxShadowTertiary,
        ...style,
      }}
      noValidate
    >
      <div>
        <h3
          id={titleId}
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: "bold",
            color: token.colorTextHeading,
            margin: 0,
          }}
        >
          Configure {DOMAIN_LABELS[domain]}
        </h3>
        <p
          style={{
            marginTop: token.marginXS,
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            margin: 0,
          }}
        >
          Select and configure your preferred provider.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            borderRadius: token.borderRadiusSM,
            border: `1px solid ${token.colorErrorBorder}`,
            backgroundColor: token.colorErrorBg,
            padding: `${token.paddingSM}px ${token.padding}px`,
            fontSize: token.fontSizeSM,
            color: token.colorText,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: token.marginSM,
        }}
      >
        <span
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            color: token.colorTextSecondary,
          }}
        >
          Provider
        </span>
        <div
          style={{
            display: "grid",
            gap: token.marginSM,
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {providers.map((entry) => {
            const isSelected = provider === entry.id;
            const tierTokens = getTierTokens(entry.tier);

            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => setProvider(entry.id)}
                aria-pressed={isSelected}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: token.margin,
                  borderRadius: token.borderRadiusLG,
                  border: `1px solid ${isSelected ? token.colorPrimaryBorderHover : token.colorBorder}`,
                  backgroundColor: isSelected
                    ? token.colorPrimaryBg
                    : token.colorBgLayout,
                  padding: token.padding,
                  textAlign: "left",
                  transition: "all 0.2s",
                  cursor: "pointer",
                  boxShadow: isSelected ? token.boxShadowSecondary : "none",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor =
                      token.colorPrimaryBorder;
                    e.currentTarget.style.backgroundColor =
                      token.colorBgContainer;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = token.colorBorder;
                    e.currentTarget.style.backgroundColor = token.colorBgLayout;
                  }
                }}
              >
                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: token.fontSizeSM,
                      fontWeight: "bold",
                      color: token.colorText,
                    }}
                  >
                    {entry.name}
                  </span>
                  <span
                    style={{
                      marginTop: token.marginXXS,
                      display: "block",
                      fontSize: 12,
                      lineHeight: 1.5,
                      color: token.colorTextSecondary,
                    }}
                  >
                    {entry.description}
                  </span>
                  <span
                    style={{
                      marginTop: token.marginSM,
                      display: "inline-flex",
                      borderRadius: token.borderRadiusSM,
                      padding: "2px 8px",
                      fontSize: 10,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      backgroundColor: tierTokens.bg,
                      color: tierTokens.text,
                    }}
                  >
                    {entry.tier}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {needsApiKey && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label
            htmlFor={`adapter-${domain}-apiKey`}
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              color: token.colorTextSecondary,
            }}
          >
            API Key
          </label>
          <input
            id={`adapter-${domain}-apiKey`}
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={`Enter ${selectedMeta?.name ?? provider} API key`}
            autoComplete="off"
            style={{
              width: "100%",
              borderRadius: token.borderRadiusSM,
              border: `1px solid ${token.colorBorder}`,
              backgroundColor: token.colorBgContainer,
              padding: "10px 16px",
              fontSize: token.fontSizeSM,
              color: token.colorText,
              outline: "none",
              transition: "all 0.2s",
              boxShadow: token.boxShadowTertiary,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = token.colorPrimary;
              e.currentTarget.style.boxShadow = `0 0 0 2px ${token.colorPrimaryBg}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = token.colorBorder;
              e.currentTarget.style.boxShadow = token.boxShadowTertiary;
            }}
          />
        </div>
      )}

      {needsBaseUrl && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label
            htmlFor={`adapter-${domain}-url`}
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              color: token.colorTextSecondary,
            }}
          >
            Base URL
          </label>
          <input
            id={`adapter-${domain}-url`}
            type="url"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            placeholder="https://..."
            style={{
              width: "100%",
              borderRadius: token.borderRadiusSM,
              border: `1px solid ${token.colorBorder}`,
              backgroundColor: token.colorBgContainer,
              padding: "10px 16px",
              fontSize: token.fontSizeSM,
              color: token.colorText,
              outline: "none",
              transition: "all 0.2s",
              boxShadow: token.boxShadowTertiary,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = token.colorPrimary;
              e.currentTarget.style.boxShadow = `0 0 0 2px ${token.colorPrimaryBg}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = token.colorBorder;
              e.currentTarget.style.boxShadow = token.boxShadowTertiary;
            }}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: token.marginSM,
          paddingTop: token.marginXS,
        }}
      >
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              borderRadius: token.borderRadiusSM,
              padding: "10px 20px",
              fontSize: token.fontSizeSM,
              fontWeight: 600,
              color: token.colorTextSecondary,
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = token.colorText)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = token.colorTextSecondary)
            }
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSaving || !provider}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: token.borderRadiusSM,
            border: `1px solid ${token.colorPrimaryBorder}`,
            backgroundColor: token.colorPrimary,
            padding: "10px 32px",
            fontSize: token.fontSizeSM,
            fontWeight: "bold",
            color: token.colorTextLightSolid,
            boxShadow: token.boxShadowTertiary,
            cursor: isSaving || !provider ? "not-allowed" : "pointer",
            opacity: isSaving || !provider ? 0.5 : 1,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (!isSaving && provider) e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            if (!isSaving && provider) e.currentTarget.style.opacity = "1";
          }}
        >
          {isSaving ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
